import { useState, useRef, useEffect, useMemo } from "react";
import { MapPin } from "lucide-react";
import { searchCities } from "@/lib/usCities";

/**
 * Reusable US city typeahead. Renders a text <input> plus an absolutely-positioned
 * suggestion list. Consumer owns the value.
 *
 * Props:
 *   value, onChange (string, called with the selected label e.g. "Phoenix, Arizona")
 *   placeholder, required, className, inputClassName, dataTestId
 *   variant: "light" | "dark"  (styling only)
 */
export default function CityAutocomplete({
  value,
  onChange,
  placeholder = "Start typing a US city…",
  required = false,
  className = "",
  inputClassName = "",
  dataTestId = "city-autocomplete",
  variant = "light",
  showIcon = false,
  autoFocus = false,
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // Keep local query in sync when parent controls value externally.
  useEffect(() => {
    if (value !== undefined && value !== query) setQuery(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const suggestions = useMemo(() => searchCities(query, 8), [query]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (item) => {
    setQuery(item.label);
    onChange?.(item.label);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        e.preventDefault();
        select(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const darkListItem = variant === "dark";

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={dataTestId}>
      <div className="relative">
        {showIcon && (
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5B6472]" strokeWidth={1.8} />
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange?.(e.target.value);
            setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          autoFocus={autoFocus}
          data-testid={`${dataTestId}-input`}
          className={inputClassName}
          style={showIcon ? { paddingLeft: "1.9rem" } : undefined}
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul
          ref={listRef}
          role="listbox"
          data-testid={`${dataTestId}-list`}
          className={`absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-auto shadow-xl border ${
            darkListItem
              ? "bg-[#0B2545] border-white/10 text-white"
              : "bg-white border-[#E5E7EB] text-[#0B2545]"
          }`}
        >
          {suggestions.map((s, i) => {
            const active = i === activeIdx;
            return (
              <li
                key={s.label}
                role="option"
                aria-selected={active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(s);
                }}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`${dataTestId}-option-${i}`}
                className={`px-4 py-2.5 cursor-pointer text-sm flex items-center justify-between gap-3 transition-colors ${
                  active
                    ? darkListItem
                      ? "bg-white/10"
                      : "bg-[#F5F7FA]"
                    : ""
                }`}
              >
                <span className="truncate">
                  <span className="font-medium">{s.city}</span>
                  <span className={darkListItem ? "text-white/60" : "text-[#5B6472]"}>, {s.state}</span>
                </span>
                <span className={`font-mono text-[10px] tracking-wider ${darkListItem ? "text-white/50" : "text-[#94A0AE]"}`}>
                  {s.stateCode}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
